import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { PackingList as List } from '../types';

interface Props {
  tourId: number;
  onClose: () => void;
}

/**
 * Nalog za pakovanje — ono što magacioner drži u ruci dok tovari.
 *
 * Redosled je obrnut od isporuke: prva stanica u spisku ide najdublje u vozilo,
 * poslednja do vrata. Bez toga se na svakoj stanici prekopava tovar.
 *
 * <b>Redosled unutar stanice se ne izmišlja.</b> Gabarite i mase artikala nemamo
 * u bazi, pa bi svaki poredak bio nagađanje — a magacioner to zna bolje od nas.
 * Ovde stoji spisak; raspored u okviru stanice ostaje njegov.
 *
 * Štampa se, jer se u magacinu ne radi sa telefonom u ruci.
 */
export default function PackingList({ tourId, onClose }: Props) {
  const [list, setList] = useState<List | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.packingList(tourId)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [tourId]);

  if (error) {
    return <div className="health health--bad">{error}</div>;
  }

  if (!list) {
    return <p className="muted">Učitavam…</p>;
  }

  return (
    <div className="packing">
      <div className="packing__bar">
        <button type="button" onClick={() => window.print()}>Štampaj</button>
        <button type="button" className="link" onClick={onClose}>Zatvori</button>
      </div>

      <div className="packing__sheet">
        <h2>Nalog za pakovanje</h2>

        <p className="packing__head">
          <strong>{list.name}</strong> · {new Date(list.date).toLocaleDateString('sr-RS')}
          {' · '}vozilo {list.vehicle ?? '—'} · vozač {list.driver ?? '—'}
          {' · '}ukupno {list.totalPieces} kom
        </p>

        {/* Preuzimanje koje ne stoji tamo gde je roba. Na vrhu, jer traži
            potez PRE polaska — napomena uz red se lako preskoči. */}
        {(list.conflicts?.length ?? 0) > 0 && (
          <div className="blocker">
            <strong>Preuzimanje se ne slaže sa mestom robe — javi operateru pre polaska.</strong>
            <ul>
              {list.conflicts!.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
        )}

        <p className="packing__rule">
          Tovari se ovim redom: <strong>1 ide najdublje</strong>, poslednja stanica
          stoji do vrata i vadi se prva.
        </p>

        {list.stops.map((stop) => (
          <section key={stop.loadingSequence} className="packing__stop">
            <h3>
              <span className="packing__no">{stop.loadingSequence}</span>
              {stop.label}
              {stop.city ? `, ${stop.city}` : ''}
              <span className="packing__meta">
                {' '}· {stop.pieces} kom · isporuka {stop.plannedSequence}.
                {stop.isNearestDoor && ' · do vrata'}
                {stop.requiresTwoPeople && ' · DVA ČOVEKA'}
              </span>
            </h3>

            <table className="packing__items">
              <tbody>
                {stop.items.map((item, index) => (
                  <tr
                    key={`${item.articleCode}-${item.erpDocument ?? ''}-${index}`}
                    className={item.verdict === 'NijeUMagacinu' || item.verdict === 'NePoznato'
                      || item.verdict === 'PreuzimanjeNaPogresnomMestu' || item.verdict === 'UVozilu'
                      ? 'packing__row--pazi'
                      : undefined}
                  >
                    <td className="packing__qty">{item.quantity}</td>
                    <td>
                      {item.articleName}
                      {/* GDE JE ROBA, kad nije tamo gde se tovari.
                          Do 01.10.2026. je nalog nosio samo šifru, naziv, količinu
                          i dokument — a deo robe stoji drugde, pa je magacioner to
                          držao u glavi ili zvao. Stavka koja JESTE u magacinu ne
                          dobija napomenu: red koji piše ono što se podrazumeva
                          guta ono što ne treba propustiti. */}
                      {item.note && (
                        <span className="packing__note"> — {item.note}</span>
                      )}
                    </td>
                    <td className="packing__code">{item.articleCode}</td>
                    {/* Po broju naloga se roba nalazi u magacinu rezervacija. */}
                    <td className="packing__doc">{item.erpDocument ?? ''}</td>
                    {/* Prazan kvadratić: magacioner štriklira olovkom dok tovari. */}
                    <td className="packing__check">☐</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        {list.pickups.length > 0 && (
          <section className="packing__pickups">
            <h3>Ne tovari se — diže se usput</h3>

            {list.pickups.map((stop) => (
              <p key={stop.plannedSequence}>
                <strong>{stop.label}</strong>
                {stop.city ? `, ${stop.city}` : ''} — {stop.items
                  .map((i) => `${i.articleName} × ${i.quantity}`)
                  .join(', ')}
                {stop.items
                  .filter((i) => i.verdict === 'PreuzimanjeNaPogresnomMestu' && i.note)
                  .map((i) => (
                    <span key={i.articleCode} className="packing__note"> — {i.note}</span>
                  ))}
              </p>
            ))}
          </section>
        )}

        <p className="packing__sign">
          Pakovao: ______________________ &nbsp;&nbsp; Preuzeo vozač: ______________________
        </p>
      </div>
    </div>
  );
}
